import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select } from '@artosera/ui';
import { useOeuvres, useOeuvre, useEditionsOeuvre } from '../../api/oeuvres.js';
import { useEquipe } from '../../api/equipe.js';
import { pipelineApi, useActionPipeline, type AffairePresentee } from '../../api/pipeline.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ChoixContact } from './ChoixContact.js';
import styles from './Pipeline.module.css';

interface Ligne {
  oeuvreId: string;
  titre: string;
  exemplaireId?: string | null;
  montantCentimes: number;
  editionSuivie?: boolean;
}
export interface NouvelleAffaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactInitial?: { id: string; nom: string };
  oeuvreInitiale?: {
    id: string;
    titre: string;
    prixPublicCentimes: number | null;
    editionSuivie?: boolean;
  };
  affaireInitiale?: AffairePresentee;
}
export function NouvelleAffaireDialog({
  open,
  onOpenChange,
  contactInitial,
  oeuvreInitiale,
  affaireInitiale,
}: NouvelleAffaireDialogProps) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const navigate = useNavigate();
  const voitMontants =
    !galerieAModule(galerie, 'roles-fins') ||
    galerieALaction(galerie, 'artworks:read-artist-price');
  const lignesModifiables = !affaireInitiale || (!affaireInitiale.closeLe && voitMontants);
  const [contact, setContact] = useState(
    contactInitial ??
      (affaireInitiale ? { id: affaireInitiale.contactId, nom: affaireInitiale.contactNom } : null),
  );
  const [recherche, setRecherche] = useState('');
  const [lignes, setLignes] = useState<Ligne[]>(
    affaireInitiale?.lignes.map((l) => ({
      ...l,
      titre: '',
      montantCentimes: l.montantCentimes ?? 0,
      editionSuivie: Boolean(l.exemplaireId),
    })) ??
      (oeuvreInitiale
        ? [
            {
              oeuvreId: oeuvreInitiale.id,
              titre: oeuvreInitiale.titre,
              montantCentimes: oeuvreInitiale.prixPublicCentimes ?? 0,
              editionSuivie: oeuvreInitiale.editionSuivie,
            },
          ]
        : []),
  );
  const [titre, setTitre] = useState(affaireInitiale?.titre ?? '');
  const [cloture, setCloture] = useState(affaireInitiale?.clotureEspereeLe?.slice(0, 10) ?? '');
  const [responsable, setResponsable] = useState(affaireInitiale?.responsableId ?? '');
  const [probabilite, setProbabilite] = useState(affaireInitiale?.probabilite ?? 10);
  const oeuvres = useOeuvres(
    galerieSlug,
    { q: recherche, statut: ['disponible', 'reservee', 'en_depot', 'pretee'], parPage: 20 },
    { enabled: recherche.trim().length >= 2 },
  );
  const equipe = useEquipe(galerieSlug, { enabled: galerieALaction(galerie, 'memberships:read') });
  const mutation = useActionPipeline(
    galerieSlug,
    (
      corps: {
        lignes: Ligne[];
        titre: string;
        clotureEspereeLe: string | null;
        responsableId: string | null;
      },
      appel,
    ) => {
      const entree = {
        ...corps,
        lignes: corps.lignes.map(({ oeuvreId, exemplaireId, montantCentimes }) => ({
          oeuvreId,
          exemplaireId,
          montantCentimes,
        })),
      };
      if (affaireInitiale) {
        // Une modification sans droit aux montants conserve les lignes côté serveur.
        const modification = lignesModifiables
          ? entree
          : {
              titre,
              clotureEspereeLe: entree.clotureEspereeLe,
              responsableId: entree.responsableId,
            };
        return pipelineApi.modifier(
          galerieSlug,
          affaireInitiale.id,
          { ...modification, probabilite, version: affaireInitiale.version },
          appel,
        );
      }
      return pipelineApi.creer(galerieSlug, { ...entree, contactId: contact!.id }, appel);
    },
  );
  const valide =
    contact &&
    lignes.length > 0 &&
    lignes.length <= 20 &&
    !lignes.some((l) => l.editionSuivie && !l.exemplaireId) &&
    Number.isInteger(probabilite) &&
    probabilite >= 0 &&
    probabilite <= 100;
  async function soumettre() {
    if (!valide) return;
    try {
      const resultat = await mutation.mutateAsync({
        titre,
        lignes,
        clotureEspereeLe: cloture || null,
        responsableId: responsable || null,
      });
      onOpenChange(false);
      if (!affaireInitiale)
        navigate(`/${galerieSlug}/pipeline?affaire=${encodeURIComponent(resultat.affaire.id)}`);
    } catch {
      /* La mutation présente l’erreur. */
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t(affaireInitiale ? 'regie:pipeline.modifier' : 'regie:pipeline.nouvelle')}
      closeLabel={t('commun:actions.fermer')}
      description={t('regie:pipeline.descriptionAffaire')}
      footer={
        <Button loading={mutation.isPending} disabled={!valide} onClick={() => void soumettre()}>
          {t('commun:actions.enregistrer')}
        </Button>
      }
    >
      <div className={styles.formulaire}>
        {!affaireInitiale ? (
          <ChoixContact valeur={contact} onChange={setContact} />
        ) : (
          <p>{contact?.nom}</p>
        )}
        <Field htmlFor="affaire-titre" label={t('regie:pipeline.titreAffaire')}>
          <Input
            id="affaire-titre"
            maxLength={5000}
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
          />
        </Field>
        {lignesModifiables ? (
          <>
            <Field htmlFor="affaire-oeuvre" label={t('regie:pipeline.oeuvre')}>
              <Input
                id="affaire-oeuvre"
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
              />
            </Field>
            {oeuvres.data?.oeuvres
              .filter((o) => !lignes.some((l) => l.oeuvreId === o.id))
              .map((o) => (
                <Button
                  variant="ghost"
                  key={o.id}
                  disabled={lignes.length >= 20}
                  onClick={() => {
                    setLignes([
                      ...lignes,
                      {
                        oeuvreId: o.id,
                        titre: o.titre,
                        montantCentimes: o.prixPublicCentimes ?? 0,
                        editionSuivie: o.type === 'edition' && o.tirage?.exemplairesSuivis === true,
                      },
                    ]);
                    setRecherche('');
                  }}
                >
                  {o.titre} · {o.artisteNom}
                </Button>
              ))}
            {oeuvres.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
            {lignes.map((l, index) => (
              <div key={l.oeuvreId} className={styles.bloc}>
                <TitreLigne ligne={l} numero={index + 1} />
                {l.editionSuivie ? (
                  <ChoixExemplaire
                    ligne={l}
                    onChange={(id) =>
                      setLignes(
                        lignes.map((a, i) => (i === index ? { ...a, exemplaireId: id } : a)),
                      )
                    }
                  />
                ) : null}
                {voitMontants ? (
                  <Field htmlFor={`affaire-montant-${index}`} label={t('regie:pipeline.montant')}>
                    <PriceInput
                      id={`affaire-montant-${index}`}
                      cents={l.montantCentimes}
                      lang={i18n.language === 'en' ? 'en' : 'fr'}
                      onCentsChange={(v) =>
                        setLignes(
                          lignes.map((a, i) =>
                            i === index ? { ...a, montantCentimes: v ?? 0 } : a,
                          ),
                        )
                      }
                    />
                  </Field>
                ) : null}
                <Button
                  variant="ghost"
                  aria-label={t('regie:pipeline.enleverLigne', { numero: index + 1 })}
                  onClick={() => setLignes(lignes.filter((_, i) => i !== index))}
                >
                  {t('regie:pipeline.retirer')}
                </Button>
              </div>
            ))}
          </>
        ) : null}
        <Field htmlFor="affaire-cloture" label={t('regie:pipeline.cloture')}>
          <Input
            id="affaire-cloture"
            type="date"
            value={cloture}
            onChange={(e) => setCloture(e.target.value)}
          />
        </Field>
        <Field htmlFor="affaire-responsable" label={t('regie:pipeline.responsable')}>
          <Select
            id="affaire-responsable"
            value={responsable}
            onValueChange={setResponsable}
            options={[
              { value: '', label: t('regie:pipeline.sansResponsable') },
              ...(equipe.data?.membres.map((m) => ({ value: m.compteId, label: m.nom })) ?? []),
              ...(!equipe.data && affaireInitiale?.responsableId
                ? [
                    {
                      value: affaireInitiale.responsableId,
                      label: affaireInitiale.responsableNom ?? t('regie:pipeline.responsable'),
                    },
                  ]
                : []),
            ]}
          />
        </Field>
        {affaireInitiale ? (
          <Field htmlFor="affaire-probabilite" label={t('regie:pipeline.probabilite')}>
            <Input
              id="affaire-probabilite"
              type="number"
              min={0}
              max={100}
              value={probabilite}
              onChange={(e) => setProbabilite(Number(e.target.value))}
            />
          </Field>
        ) : null}
        {mutation.isError ? (
          <p role="alert">
            {estApiError(mutation.error) && mutation.error.status === 409
              ? t('regie:pipeline.conflit')
              : t('commun:erreurReseau')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
function ChoixExemplaire({ ligne, onChange }: { ligne: Ligne; onChange: (id: string) => void }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const editions = useEditionsOeuvre(galerieSlug, ligne.oeuvreId);
  return (
    <Field htmlFor={`affaire-exemplaire-${ligne.oeuvreId}`} label={t('pipeline.exemplaire')}>
      <Select
        id={`affaire-exemplaire-${ligne.oeuvreId}`}
        value={ligne.exemplaireId ?? ''}
        onValueChange={onChange}
        options={(editions.data?.editions ?? [])
          .filter((e) => ['disponible', 'reservee', 'en_depot', 'pretee'].includes(e.statut))
          .map((e) => ({ value: e.id, label: e.numero }))}
      />
    </Field>
  );
}

function TitreLigne({ ligne, numero }: { ligne: Ligne; numero: number }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const oeuvre = useOeuvre(
    galerieSlug,
    !ligne.titre && galerieALaction(galerie, 'artworks:read') ? ligne.oeuvreId : null,
  );
  return <p>{ligne.titre || oeuvre.data?.oeuvre.titre || t('pipeline.ligne', { numero })}</p>;
}
